import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Modal, Spin, Typography } from 'antd'
import { find, isEmpty, isNil, map } from 'lodash'

import { GridFormItem } from '../../../core/components/sections'
import { showErrorNotification } from '../../../core/helpers'

import { Select } from '../../../common/components/sections'
import { StyledPaddingBottomRow } from '../../../common/components/styled'
import { ROLES } from '../../../common/constants'

import { useUserHasRoles } from '../../../account/hooks'
import { useActivePriorityTasksLazyQuery } from '../../../priority-tasks/hooks'
import { ALLOCATION_TIME_OPTIONS } from '../../constants'
import { useAllocateAutomaticallyMutation } from '../../hooks'

const MenuCurationTasksUserAllocationModal = () => {
  const [isVisible, setIsVisible] = useState(false)
  const [allocateTime, setAllocateTime] = useState(null)
  const [selectedPriorityTask, setSelectedPriorityTask] = useState(null)

  const userCanUseAutomaticAllocation = useUserHasRoles([
    ROLES.CURATOR.value,
    ROLES.QA.value,
    ROLES.QA_TRAINEE.value,
    ROLES.QC.value,
    ROLES.QC_TRAINEE.value,
  ])

  const [
    fetchActiveTasks,
    { data, isLoading: isFetchLoading, error: fetchError },
  ] = useActivePriorityTasksLazyQuery()

  const [allocate, { isLoading: isAllocateLoading, error: allocateError }] =
    useAllocateAutomaticallyMutation()

  useEffect(() => {
    if (fetchError) {
      showErrorNotification({
        title: 'Failed to get priority task information',
        description: fetchError,
      })
    }
  }, [fetchError])

  useEffect(() => {
    if (allocateError) {
      showErrorNotification({
        title: 'Failed to allocate user',
        description: allocateError,
      })
    }
  }, [allocateError])

  useEffect(() => {
    if (!isEmpty(data)) {
      setSelectedPriorityTask(data[0])
    }
  }, [data])

  const isAllocateButtonDisabled = useMemo(
    () =>
      isAllocateLoading ||
      isNil(selectedPriorityTask) ||
      (isNil(allocateTime) &&
        isNil(selectedPriorityTask?.customRequiredDishCount)),
    [isAllocateLoading, selectedPriorityTask, allocateTime],
  )

  const priorityTaskOptions = useMemo(
    () => map(data, ({ id, name }) => ({ label: name, value: id })),
    [data],
  )

  const handleCancel = useCallback(() => {
    setIsVisible(false)
    setAllocateTime(null)
  }, [])

  const toggleModal = useCallback(() => {
    if (!isVisible) {
      fetchActiveTasks()
    }

    setIsVisible(!isVisible)
  }, [fetchActiveTasks, isVisible])

  const handleAllocate = useCallback(async () => {
    if (selectedPriorityTask?.id) {
      await allocate(selectedPriorityTask.id, allocateTime)
      handleCancel()
    }
  }, [allocate, allocateTime, selectedPriorityTask, handleCancel])

  const handleAllocateTimeChange = useCallback(
    value => setAllocateTime(value),
    [],
  )

  const handlePriorityTaskChange = useCallback(
    value => setSelectedPriorityTask(find(data, { id: value })),
    [data],
  )

  useEffect(() => {
    if (userCanUseAutomaticAllocation) {
      fetchActiveTasks()
    }
  }, [fetchActiveTasks, userCanUseAutomaticAllocation])

  if (!userCanUseAutomaticAllocation || isEmpty(data)) {
    return null
  }

  return (
    <>
      <Modal
        footer={null}
        visible={isVisible}
        wrapClassName="menu-curation-task-allocation-modal"
        onCancel={handleCancel}
      >
        {isFetchLoading ? (
          <Spin />
        ) : (
          <>
            <Typography.Title level={4}>Allocation</Typography.Title>
            <StyledPaddingBottomRow gutter={16}>
              <GridFormItem span={12} label="Selected priority task">
                <Select
                  options={priorityTaskOptions}
                  value={selectedPriorityTask?.id}
                  onChange={handlePriorityTaskChange}
                />
              </GridFormItem>
            </StyledPaddingBottomRow>
            <Typography.Paragraph>
              <Typography.Text strong>Description:</Typography.Text>{' '}
              {selectedPriorityTask?.description}
            </Typography.Paragraph>
            <StyledPaddingBottomRow gutter={16}>
              {isNil(selectedPriorityTask?.customRequiredDishCount) && (
                <GridFormItem span={12} label="Allocate time">
                  <Select
                    options={ALLOCATION_TIME_OPTIONS}
                    value={allocateTime}
                    onChange={handleAllocateTimeChange}
                  />
                </GridFormItem>
              )}
            </StyledPaddingBottomRow>
            <Button
              onClick={handleAllocate}
              loading={isAllocateLoading}
              disabled={isAllocateButtonDisabled}
            >
              Allocate
            </Button>
          </>
        )}
      </Modal>
      <Button onClick={toggleModal}>Automatic allocation</Button>
    </>
  )
}

export default MenuCurationTasksUserAllocationModal
